import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import camelcaseKeys from 'camelcase-keys';
import classNames from 'classnames';
import sanitizeHtml from 'sanitize-html';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import queryString from 'query-string';
import moment from 'moment';
import percentIcon from '@src/assets/icons/percentIcon.svg';
import fireIcon from '@src/assets/icons/fireIcon.svg';
import { checkVietNam } from '@src/utils/checkCountry';

import apis from '@src/apis';
import {
  PACK_DURATION,
  PACKAGE_EXPIRED,
  PACKAGE_LEVEL,
  PACKAGE_TYPE,
  TOP_UP_PACKAGE_CODES,
} from '@src/constants/package';
import actions from '@src/redux/actions';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  MenuItem,
  MenuList,
  RadioGroup,
  Skeleton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ArrowDropUpRounded, ArrowDropDownRounded } from '@mui/icons-material';
import { LANGUAGE } from '@src/constants';
import CustomRadio from '@src/components/CustomRadio';
import { COLOR } from '@src/styles/color';
import { BREAKPOINTS } from '@src/styles/config';
import theme from '@src/styles/theme';
import {
  PAYMENT_HUB_RENEWAL_TYPE,
  SUBSCRIPTION_PURCHASE_STATUS,
} from '@src/constants/subscription';
import { PAYMENT_METHOD_TYPE } from '@src/constants/payment';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import {
  checkCustomPackage,
  getDefaultValueCustomResource,
  getPackageCurrentPrice,
  getPackageDataDuration,
  getTotalPricePackage,
  roundSaleOffPercentage,
} from '@src/services/package';
import Tooltip from '@src/components/Tooltip';
import { getSavedPrice, verifyPolicy } from '@src/services/voucher';
import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import { VOUCHER_TYPE } from '@src/constants/voucher';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import {
  StyledPackageInfoItem,
  StyledPackageInfo,
  StyledPopover,
} from './index.style';
import BuyExtracharacters from './BuyExtracharacters';
import CustomResources from './CustomResources';

const DURATION_REGEX = {
  MONTHLY: /MONTH/i,
  QUARTERLY: /QUART/i,
  YEARLY: /YEAR/i,
};

const isSameDuration = (p1, p2) => {
  if (DURATION_REGEX.MONTHLY.test(p1) && DURATION_REGEX.MONTHLY.test(p2))
    return true;
  if (DURATION_REGEX.QUARTERLY.test(p1) && DURATION_REGEX.QUARTERLY.test(p2))
    return true;
  if (DURATION_REGEX.YEARLY.test(p1) && DURATION_REGEX.YEARLY.test(p2))
    return true;
  return false;
};

const getDurationLabel = (pkgCode) => {
  const isMonthly = DURATION_REGEX.MONTHLY.test(pkgCode);
  const isQuarterly = DURATION_REGEX.QUARTERLY.test(pkgCode);
  const isYearly = DURATION_REGEX.YEARLY.test(pkgCode);
  if (isMonthly) return 'month';
  if (isQuarterly) return 'quarter';
  if (isYearly) return 'year';

  return '';
};

const getSubscriptionPeriod = (duration, isAutoRenew) => {
  const periodMap = {
    [PACK_DURATION.MONTH]: {
      oneTime: 'oneMonth',
      autoRenew: 'monthly',
    },
    [PACK_DURATION.YEAR]: {
      oneTime: 'oneYear',
      autoRenew: 'yearly',
    },
  };

  const translationKey =
    periodMap[duration]?.[isAutoRenew ? 'autoRenew' : 'oneTime'];
  return translationKey || '';
};

const PackageDuration = ({
  showTooltip,
  disabledPackageAutoRenewUsing,
  subscriptionPeriodKey,
  actualPrice,
  handleShowPopupCannotBuyPackage,
  originalPrice,
  saleOffPercentageRounded,
  packageCode,
  isShowBestPrice,
  hasSaleOff,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.SMD));
  const isVietNam = checkVietNam();

  const Price = ({
    price,
    lineThrough = false,
    duration = '',
    isOriginalPrice = false,
  }) => (
    <div className="price">
      {isVietNam && (
        <span
          className={`underline ${
            lineThrough || duration || isOriginalPrice
              ? 'original-price-unit font-size-14'
              : 'discount-price-unit font-size-16'
          }`}
        >
          đ
        </span>
      )}
      {!isVietNam && (
        <span
          className={`${
            lineThrough || duration || isOriginalPrice
              ? 'original-price-unit font-size-14'
              : 'discount-price-unit font-size-14'
          }`}
        >
          $
        </span>
      )}
      {isOriginalPrice ? (
        <span
          className={`original-price ${lineThrough ? 'line-through ' : ''}`}
        >
          {`${price?.toLocaleString(language)}${
            duration.length > 0 ? `/${duration}` : ''
          }`}
        </span>
      ) : (
        <span className="discount-price">
          {price?.toLocaleString(language)}
        </span>
      )}
    </div>
  );

  const PackageNameMobile = () => (
    <div className="title-wrapper">
      <div className="flex flex-col">
        <div className="flex align-item-center">
          <div className="title">{t(subscriptionPeriodKey)}</div>
          {hasSaleOff && !disabledPackageAutoRenewUsing && (
            <div className="tag-discount">
              <img
                src={percentIcon}
                className="percent-icon"
                alt="percent-icon"
              />
              {t('percentDiscount', {
                percentDiscount: saleOffPercentageRounded,
              })}
            </div>
          )}
        </div>

        {isShowBestPrice && !disabledPackageAutoRenewUsing && (
          <div className="mt-1 most-popular banner">
            <img className="fire-icon" src={fireIcon} alt="fireIcon" />
            <span> {t('bestPrice')}</span>
          </div>
        )}
      </div>
      {showTooltip && (
        <Tooltip arrow placement="top">
          <InfoRoundedIcon
            onClick={(e) => {
              e.stopPropagation();
              handleShowPopupCannotBuyPackage();
            }}
            fontSize="16px"
            className="icon-info-rounded"
          />
        </Tooltip>
      )}
    </div>
  );

  const PackageName = () => (
    <div className="title-wrapper">
      <div className="title">{t(subscriptionPeriodKey)}</div>
      <>
        {hasSaleOff && !disabledPackageAutoRenewUsing && (
          <div className="tag-discount">
            <img
              src={percentIcon}
              className="percent-icon"
              alt="percent-icon"
            />
            {t('percentDiscount', {
              percentDiscount: saleOffPercentageRounded,
            })}
          </div>
        )}

        {isShowBestPrice && !disabledPackageAutoRenewUsing && (
          <div className="most-popular banner">
            <img className="fire-icon" src={fireIcon} alt="fireIcon" />
            <span> {t('bestPrice')}</span>
          </div>
        )}
      </>
      {showTooltip && (
        <Tooltip
          arrow
          placement="top"
          title={
            <div
              role="button"
              tabIndex="0"
              onClick={(e) => e.stopPropagation()}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  t('goToMyPackage', {
                    color: '#fff',
                  }),
                  {
                    allowedTags: ['a', 'div'],
                    allowedAttributes: {
                      div: ['style'],
                      a: ['href', 'target', 'style'],
                    },
                  },
                ),
              }}
            />
          }
        >
          <InfoRoundedIcon
            onClick={(e) => {
              e.stopPropagation();
              handleShowPopupCannotBuyPackage();
            }}
            fontSize="16px"
            className="icon-info-rounded"
          />
        </Tooltip>
      )}
    </div>
  );

  const PackagePriceMonth = () => (
    <div className="price-wrapper">
      <Price price={actualPrice} />
    </div>
  );

  const PackageThrifty = () => (
    <Box>
      <div className="flex flex-end text-14 text-dark-v2-64">
        <Price
          lineThrough
          price={originalPrice}
          duration={t(getDurationLabel(packageCode))}
          isOriginalPrice
        />
      </div>
    </Box>
  );

  return (
    <StyledPackageInfoItem isMobile={isMobile}>
      <div
        className={classNames('package-info-title-and-price', {
          'align-item-center': isMobile,
        })}
      >
        {isMobile ? <PackageNameMobile /> : <PackageName />}
      </div>
      {disabledPackageAutoRenewUsing ? (
        <div className="using-text">{t('using')}</div>
      ) : (
        <div className="flex-col align-item-end">
          <PackagePriceMonth />
          {originalPrice !== actualPrice && <PackageThrifty />}
        </div>
      )}
    </StyledPackageInfoItem>
  );
};

const PackageDurationSelect = ({
  usingOrders,
  getPrice,
  sameLevelPackages,
  onSelectPackage,
  selectedPackage,
}) => {
  const getPackageKey = (code, isAutoRenew) => `${code}-${isAutoRenew}`;

  const getBestPercentVoucher = Math.max(
    ...sameLevelPackages?.map(
      (pkg) => getPrice(pkg)?.saleOffPercentageRounded || 0,
    ),
  );

  const [openPopupCannotBuyPackage, setOpenPopupCannotBuyPackage] =
    useState(false);

  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.SMD));

  const handleShowPopupCannotBuyPackage = () => {
    if (!isMobile) return;
    setOpenPopupCannotBuyPackage(true);
  };

  return (
    <div className="package-duration-select">
      <RadioGroup
        value={getPackageKey(
          selectedPackage?.code,
          selectedPackage?.isAutoRenew,
        )}
        aria-labelledby="row-radio-buttons-group-package-duration"
        name="row-radio-buttons-group"
        className="flex-col"
      >
        {sameLevelPackages?.map((pkg) => {
          const { originalPrice, actualPrice, saleOffPercentageRounded } =
            getPrice(pkg) || {};

          const subscriptionPeriodKey = getSubscriptionPeriod(
            pkg?.expiresIn,
            pkg?.isAutoRenew,
          );

          const hasSaleOff = Boolean(saleOffPercentageRounded);

          const { usingPackage, subscriptionPurchase } = useSelector(
            (state) => state.user || {},
          );
          const { user } = useSelector((state) => state.auth) || {};
          const usingStudioOrder = usingOrders?.find(
            (order) => order?.id === user?.orderId,
          );
          const renewwalType =
            usingStudioOrder?.renewalType ===
            PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW;
          const hasSamePackage = usingPackage?.code === pkg?.code;
          const isAutoRenewSubscription = pkg?.isAutoRenew;
          const activeAutoRenew =
            subscriptionPurchase?.status ===
            SUBSCRIPTION_PURCHASE_STATUS.ACTIVE;
          const isBuyOnApp = [
            PAYMENT_METHOD_TYPE.APPLE,
            PAYMENT_METHOD_TYPE.GOOGLE,
          ].includes(usingStudioOrder?.paymentMethod?.type);

          const samePackage =
            hasSamePackage && isAutoRenewSubscription && renewwalType;

          let disabledPackageAutoRenewUsing = false;
          if (isBuyOnApp && samePackage) {
            disabledPackageAutoRenewUsing = activeAutoRenew;
          }
          if (!isBuyOnApp && samePackage) {
            disabledPackageAutoRenewUsing = !!subscriptionPurchase;
          }

          return (
            <Box
              key={getPackageKey(pkg?.code, pkg?.isAutoRenew)}
              onClick={() => onSelectPackage(pkg)}
              data-id="option-plan-duration"
              className={disabledPackageAutoRenewUsing && 'disabled'}
              data-package-code={pkg.code}
              data-duration={getPackageDataDuration(pkg.code, pkg?.isAutoRenew)}
            >
              <div className="flex align-item-center pb-8 pt-8">
                <FormControlLabel
                  value={getPackageKey(pkg?.code, pkg?.isAutoRenew)}
                  control={
                    <CustomRadio
                      sx={{ paddingLeft: '11px', marginTop: '3px' }}
                    />
                  }
                  className={
                    disabledPackageAutoRenewUsing
                      ? 'disabled-checkbox'
                      : 'label-checkbox'
                  }
                  label=""
                />

                <PackageDuration
                  showTooltip={samePackage && !isBuyOnApp && !activeAutoRenew}
                  disabledPackageAutoRenewUsing={disabledPackageAutoRenewUsing}
                  isShowBestPrice={
                    getBestPercentVoucher === saleOffPercentageRounded
                  }
                  handleShowPopupCannotBuyPackage={
                    handleShowPopupCannotBuyPackage
                  }
                  subscriptionPeriodKey={subscriptionPeriodKey}
                  saleOffPercentageRounded={saleOffPercentageRounded}
                  actualPrice={actualPrice}
                  packageCode={pkg?.code}
                  originalPrice={originalPrice}
                  hasSaleOff={hasSaleOff}
                />
              </div>
              <Divider />
            </Box>
          );
        })}
      </RadioGroup>
      <NotificationDialogV3
        name={t('noticePopup')}
        title={t('noticePopup')}
        description={t('goToMyPackage', {
          color: '#000000',
        })}
        open={openPopupCannotBuyPackage}
        onClose={() => setOpenPopupCannotBuyPackage(false)}
        fitWidth
        variant="null"
        actionComponent={
          <Button
            onClick={() => setOpenPopupCannotBuyPackage(false)}
            className="btn-close-popup"
            variant="contained"
          >
            {t('gotIt')}
          </Button>
        }
      />
    </div>
  );
};

const PackageInfo = ({
  packages,
  setPackages,
  usingOrders,
  calculateCharacterPurchase,
  currentNumberCharacters,
  onChangeNumberCharacters,
  onChangeSelectedPackageCode,
  selectedPackageCode,
}) => {
  const history = useHistory();
  const isDubbingRoute = useCheckDubbingRoute();

  const { package: packageState } = useSelector((state) => state.order);
  const dispatch = useDispatch();

  const [selectedPackage, setSelectedPackage] = useState({});
  const [loading, setLoading] = useState(true);
  const [isFetchingVouchers, setIsFetchingVouchers] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const { i18n } = useTranslation();
  const { language } = i18n;
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { getFeatureValue } = useFeatureFlags();
  const isVietNam = checkVietNam();

  const uiBuyTopupCharacters = getFeatureValue(
    FEATURE_KEYS.UI_BUY_TOP_UP_CHARACTERS,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const useVoucherForAutoRenew = getFeatureValue(
    FEATURE_KEYS.VOUCHER_FOR_AUTO_RENEW,
    { userId: user.id },
  );

  const {
    package: packageCode,
    voucherCode,
    bannerType,
    autoRenew,
    credits: creditsQuery,
    devices: devicesQuery,
    voiceClonings: voiceCloningsQuery,
  } = camelcaseKeys(queryString.parse(location.search) || {});

  const autoRenewParam = useMemo(() => {
    if (autoRenew) {
      try {
        return JSON.parse(autoRenew);
      } catch (e) {
        return null;
      }
    }
    return false;
  }, [autoRenew]);

  const openPopover = Boolean(anchorEl);
  const id = openPopover ? 'simple-popover' : undefined;

  const handleClickPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => setAnchorEl(null);

  const changeRoute = ({
    code,
    isAutoRenew,
    credits,
    devices,
    voiceClonings,
  }) => {
    const search = queryString.stringify({
      package: code,
      autoRenew: Boolean(isAutoRenew),
      voucherCode,
      bannerType,
      ...(credits && { credits }),
      ...(devices && { devices }),
      ...(voiceClonings && { voiceClonings }),
    });
    history.replace({ search });
  };

  const handleSelectLevel = (item) => {
    const curPackage = packages.find((p) => p?.code === selectedPackage?.code);

    const sameLevelPackages = packages.filter((p) => p?.level === item?.level);
    let newSelectedPackage = sameLevelPackages.find((p) =>
      isSameDuration(p.code, curPackage?.code),
    );

    if (item?.level === PACKAGE_LEVEL.CUSTOM) {
      newSelectedPackage = item;
    }

    const { defaultCredits, defaultDevices, defaultVoiceClonings } =
      getDefaultValueCustomResource(newSelectedPackage?.customResource) || {};

    setSelectedPackage(newSelectedPackage);
    dispatch(actions.order.updatePackage(newSelectedPackage));
    changeRoute({
      code: newSelectedPackage?.code,
      isAutoRenew: newSelectedPackage?.isAutoRenew,
      credits: defaultCredits,
      devices: defaultDevices,
      voiceClonings: defaultVoiceClonings,
    });
  };

  const handleSelectPackage = (item) => {
    const newLevelPackage = packages.find(
      (p) => p.code === item?.code && p?.isAutoRenew === item?.isAutoRenew,
    );
    const { defaultCredits, defaultDevices, defaultVoiceClonings } =
      getDefaultValueCustomResource(newLevelPackage?.customResource) || {};

    setSelectedPackage(item);
    dispatch(actions.order.updatePackage(newLevelPackage));

    changeRoute({
      code: item?.code,
      isAutoRenew: item?.isAutoRenew,
      credits: defaultCredits,
      devices: defaultDevices,
      voiceClonings: defaultVoiceClonings,
    });
  };

  const getUniqueLevelPackages = (packageList = []) => {
    const uniquePackages = [];

    packageList
      .filter(
        (item) =>
          isSameDuration(item.code, selectedPackage?.code) ||
          item?.level === PACKAGE_LEVEL.CUSTOM,
      )
      .forEach((item) => {
        if (!uniquePackages.find((p) => p.level === item?.level)) {
          uniquePackages.push(item);
        }
      });

    return uniquePackages;
  };

  const fetchPackages = async () => {
    setLoading(true);
    const res = await apis.packages.getPackages({
      type: isDubbingRoute ? PACKAGE_TYPE.DUBBING : PACKAGE_TYPE.STUDIO,
      purchasable: true,
    });
    if (res?.status) {
      const { result = [] } = res;
      const packageList = [];

      result?.packages.forEach((item) => {
        if (!item.active) return;
        if ([PACKAGE_LEVEL.TRIAL, PACKAGE_LEVEL.BASIC].includes(item.level))
          return;
        if (item.originalPackageCode) {
          const originalPackage = result?.packages.find(
            (pkgItem) => pkgItem.code === item.originalPackageCode,
          );
          if (originalPackage) packageList.push({ ...item, originalPackage });
          return;
        }
        packageList.push(item);
      });

      const subscriptions = packageList?.reduce((acc, pkg) => {
        acc.push({ ...pkg, isAutoRenew: false });

        const hasAutoRenew = pkg?.autoRenew && pkg?.autoRenew?.active;
        if (hasAutoRenew && isVietNam) {
          const { usdPrice, vndPrice } = pkg.autoRenew;
          acc.push({
            ...pkg,
            isAutoRenew: true,
            price: vndPrice,
            monthlyPrice: vndPrice,
            usdPrice,
            usdMonthlyPrice: usdPrice,
          });
        }

        return acc;
      }, []);

      setPackages(subscriptions);

      let packageSelected;
      if (selectedPackageCode) {
        packageSelected = subscriptions?.find(
          (item) => item.code === selectedPackageCode,
        );
      } else {
        packageSelected = subscriptions?.find(
          (item) =>
            item.code === packageCode && item?.isAutoRenew === autoRenewParam,
        );
      }

      if (packageSelected) {
        setSelectedPackage(packageSelected);
        dispatch(actions.order.updatePackage(packageSelected));
      }
      if (selectedPackageCode) onChangeSelectedPackageCode('');
    }
    setLoading(false);
  };

  const filterUseableVoucher = (currPackageId, isAutoRenewPkg) =>
    vouchers
      .filter((voucher) => {
        const satisfiedPolicy = verifyPolicy(voucher, currPackageId);

        const isVoucherAutoRenew =
          isAutoRenewPkg &&
          voucher.policy?.renewalType !== PAYMENT_HUB_RENEWAL_TYPE.PREPAID;
        const isVoucherPrepaid =
          !isAutoRenewPkg &&
          voucher.policy?.renewalType !== PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW;
        const isPriceOrPercentVoucher =
          voucher.type === VOUCHER_TYPE.PERCENT ||
          voucher.type === VOUCHER_TYPE.PRICE;

        const canUse =
          satisfiedPolicy &&
          !voucher.isPrivate &&
          isPriceOrPercentVoucher &&
          (!useVoucherForAutoRenew || isVoucherAutoRenew || isVoucherPrepaid);

        return canUse;
      })
      .sort((a) => (a.canUse ? -1 : 1));

  const getBestVoucherAndPackagePrice = (pkg) => {
    const canUseVouchers = filterUseableVoucher(pkg?.id, pkg?.isAutoRenew);
    const { startAt, endAt } = pkg.discountConfig || {};
    const isDiscountPackage = startAt && moment().isBetween(startAt, endAt);
    const { price, usdPrice } = getPackageCurrentPrice(pkg, true);

    const listVoucherSortByRanking = canUseVouchers
      .map((voucherItem) => {
        const reducedPrice = getSavedPrice({
          voucher: voucherItem,
          currPrice: isDiscountPackage
            ? pkg?.discountConfig?.price || price
            : price,
          currUsdPrice: isDiscountPackage
            ? pkg?.discountConfig?.usdPrice || usdPrice
            : usdPrice,
        });
        return { ...voucherItem, reducedPrice };
      })
      .sort((a, b) => b.reducedPrice - a.reducedPrice);

    const bestVoucher = listVoucherSortByRanking.length
      ? listVoucherSortByRanking[0]
      : null;

    return {
      actualPrice: getTotalPricePackage(
        pkg,
        isDiscountPackage,
        bestVoucher,
        isVietNam,
      ),
      bestVoucher,
      isDiscountPackage,
    };
  };

  const sameLevelPackages = packages
    .filter((pkg) => pkg?.level === packageState?.level && !pkg?.isTopUp)
    .sort((pkg1, pkg2) => pkg1.expiresIn - pkg2.expiresIn);

  const getCorrespondMonthPkg = () =>
    sameLevelPackages.find(
      (pkg) => pkg?.expiresIn === PACKAGE_EXPIRED.MONTHLY && !pkg?.isAutoRenew,
    );

  const getPrice = (pkg) => {
    const correspondMonthPkg = getCorrespondMonthPkg();

    const correspondMonthPrice = isVietNam
      ? correspondMonthPkg?.price
      : correspondMonthPkg?.usdPrice;

    const { actualPrice } = getBestVoucherAndPackagePrice(pkg);

    const monthByDuration = Math.round(pkg?.expiresIn / 30);
    const originalPrice = correspondMonthPrice * monthByDuration;

    const saleOffPercentage = (1 - actualPrice / originalPrice) * 100;
    const saleOffPercentageRounded = roundSaleOffPercentage(saleOffPercentage);

    return {
      originalPrice,
      actualPrice,
      saleOffPercentageRounded,
    };
  };

  const getOptionsResource = (ranges, step) => {
    const options = [];
    (ranges || []).forEach((range) => {
      for (let value = range.min; value <= range.max; value += step) {
        options.push(value.toString());
      }
    });

    return options || [];
  };

  const validateResourceOption = (resourcePath, queryValue) => {
    const options = getOptionsResource(
      packageState?.customResource?.[resourcePath]?.prices,
      packageState?.customResource?.[resourcePath]?.step,
    );
    if (!options.includes(queryValue)) history.replace(location.pathname);
  };

  // Check package exist and replace history
  const validatePackageAndResources = (type, typeString) => {
    if (location.pathname.includes(typeString)) {
      const packageExist = packages?.find(
        (item) => item.code === packageCode && item.type === type,
      );
      if (!packageExist) {
        history.replace(location.pathname);
        return;
      }

      const isCustomPackage = checkCustomPackage({
        type: packageState.type,
        level: packageState.level,
      });
      if (!isCustomPackage) return;

      // If package is custom, validate resource options
      validateResourceOption('credits', creditsQuery);
      validateResourceOption('devices', devicesQuery);
      validateResourceOption('voiceClonings', voiceCloningsQuery);
    }
  };

  const fetchVouchers = async () => {
    setIsFetchingVouchers(true);
    const data = await apis.vouchers.getVouchers();
    if (data.status) {
      setVouchers(data.result);
    }
    setIsFetchingVouchers(false);
  };

  useEffect(() => {
    fetchVouchers();
  }, []);

  useEffect(() => {
    // check complete load list packages
    if (!loading) {
      validatePackageAndResources(PACKAGE_TYPE.DUBBING, 'dubbing');
      validatePackageAndResources(PACKAGE_TYPE.STUDIO, 'studio');
    }
  }, [loading, packageCode, creditsQuery, devicesQuery, voiceCloningsQuery]);

  useEffect(() => {
    fetchPackages();
  }, [selectedPackageCode, isVietNam]);

  if (uiBuyTopupCharacters && TOP_UP_PACKAGE_CODES.includes(packageCode))
    return (
      <BuyExtracharacters
        calculateCharacterPurchase={calculateCharacterPurchase}
        onChangeNumberCharacters={onChangeNumberCharacters}
        currentNumberCharacters={currentNumberCharacters}
        packageCode={packageCode}
        packages={packages}
      />
    );

  const PackageContent = () => {
    const isCustomPackage = checkCustomPackage({
      type: packageState.type,
      level: packageState.level,
    });

    return isCustomPackage ? (
      <CustomResources />
    ) : (
      <PackageDurationSelect
        usingOrders={usingOrders}
        sameLevelPackages={sameLevelPackages}
        getPrice={getPrice}
        onSelectPackage={handleSelectPackage}
        selectedPackage={selectedPackage}
      />
    );
  };

  return (
    <StyledPackageInfo>
      {loading ? (
        <Box className="package-header vertical-center">
          <Skeleton
            animation="wave"
            variant="circular"
            width={48}
            height={48}
            sx={{ bgcolor: COLOR.grayV2[100] }}
          />
          <Skeleton
            animation="wave"
            width={150}
            height={48}
            sx={{ bgcolor: COLOR.grayV2[100] }}
          />
        </Box>
      ) : (
        <Box className="package-header vertical-center">
          <Box
            display="flex"
            gap="6px"
            onClick={handleClickPopover}
            data-id="open-package-type"
          >
            <div className="vertical-center">
              <Typography className="package-name">
                {packageState?.name &&
                  (packageState?.name[language] ||
                    packageState?.name[LANGUAGE.EN])}
              </Typography>
            </div>
            <Box className="vertical-center">
              {openPopover ? <ArrowDropUpRounded /> : <ArrowDropDownRounded />}
            </Box>
          </Box>
          <Box />
        </Box>
      )}
      <StyledPopover
        id={id}
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{ marginTop: '8px' }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow:
              '0px 40px 80px 0px rgba(0, 0, 0, 0.2), 0px 7px 10px 0px rgba(0, 0, 0, 0.09)',
          },
        }}
      >
        <MenuList className="menu" onClick={handleClosePopover}>
          {getUniqueLevelPackages(packages).map((item) => (
            <MenuItem
              className={`menu-item ${
                item.level === packageState?.level && 'selected'
              }`}
              key={item.code}
              onClick={() => handleSelectLevel(item)}
              data-id="option-plan"
              data-package-code={item.code}
            >
              <div className="vertical-center">
                <div className="name">
                  {item?.name &&
                    (item?.name[language] || item?.name[LANGUAGE.EN])}
                </div>
              </div>
            </MenuItem>
          ))}
        </MenuList>
      </StyledPopover>

      <Box>
        {loading || isFetchingVouchers ? (
          <Skeleton
            animation="wave"
            width="100%"
            height={210}
            sx={{ bgcolor: COLOR.grayV2[100] }}
          />
        ) : (
          <PackageContent />
        )}
      </Box>
    </StyledPackageInfo>
  );
};

export default PackageInfo;
